import { useEffect, useRef, useState } from 'react';
import Router from 'next/router';
import api from 'api';
import { useDispatch, useReduxState } from 'hooks';
import { applyAuthenticatedSession } from 'lib/auth';
import { Alert, Button, Link } from 'components/common';
import { Form, FormGroup, Text } from 'components/common/form';
import { LoginTwoFactor } from '.';

interface Props {
  isOpen?: boolean;
  next?: string;
  onOpenClick?: (type: string) => void;
  onSuccess?: () => void;
  openButtonText?: string;
  registrationLinkEnabled?: boolean;
  registrationLinkOnClick?: () => void;
  sessionUserUsername?: string;
}

const LocalLogin = ({
  isOpen,
  next,
  onOpenClick,
  onSuccess,
  openButtonText,
  registrationLinkEnabled,
  registrationLinkOnClick,
  sessionUserUsername
}: Props) => {
  const inputContainer = useRef<HTMLDivElement>(undefined);

  const { dispatch } = useDispatch();

  const { language, messages, serverConfig, theme } = useReduxState();

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isTwoFactor, setIsTwoFactor] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      inputContainer.current?.querySelector('input')?.focus();
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      inputContainer.current?.querySelector('input')?.focus();
    }
  }, [isOpen]);

  const handleOpen = () => {
    onOpenClick('local');
  };

  const handleTwoFactorCancel = () => {
    setError(messages.authLockedAfterFailedLogins);
    setIsSubmitting(false);
    setIsTwoFactor(false);
  };

  const handleSubmit = async (values) => {
    try {
      setError(null);
      setIsSubmitting(true);

      const response = await api.post('/auth/local/authenticate', {
        email: values.email,
        emailOrUsername: values.emailOrUsername,
        password: values.password,
        username: sessionUserUsername || values.username
      });

      if (response.data?.success && response.data?.requireTwoFactor) {
        setIsTwoFactor(true);
      } else if (response.data?.success) {
        await applyAuthenticatedSession({
          dispatch,
          user: response.data.user
        });

        if (response.data?.requireTwoFactorSetup) {
          return Router.push(`/setup-two-factor${next ? `?next=${next}` : ''}`);
        } else if (onSuccess) {
          onSuccess();
        } else if (response.data?.requirePasswordChange) {
          return Router.push(
            `set-password?key=${response.data.user.resetPasswordKey}`
          );
        } else {
          window.location.href = next || '/';
        }
      } else {
        if (response.data?.error === 'password_expired') {
          return Router.push('/reset-password?expired=true');
        }

        let error = messages.commonError;
        if (response.data) {
          if (
            response.data.error === 'user_not_found' ||
            response.data.error === 'Missing credentials'
          ) {
            error = serverConfig.accountEnableUsernameAccounts
              ? messages.authSignInUsernameError
              : messages.authSignInError;
          } else if (response.data.error === 'account_expired') {
            error = messages.authAccountExpired;
          } else if (response.data.error === 'account_locked') {
            error = messages.authAccountLocked;
          } else if (response.data.error === 'too_many_failed_logins') {
            error =
              `${messages.authTooManyFailedLoginsStart} ` +
              `${response.data.lockTemporarilyInMinutes} ` +
              messages.authTooManyFailedLoginsEnd;
          } else if (response.data.error === 'locked_after_failed_logins') {
            error = messages.authLockedAfterFailedLogins;
          } else if (response.data.error === 'admin_sso_required') {
            error = messages.authAdminSsoRequired;
          }
        }
        setError(error);
        setIsSubmitting(false);
      }
    } catch (err) {
      setError(messages.commonError);
      setIsSubmitting(false);
    }
  };

  const {
    accountEnableUsernameAccounts,
    authLocalLoginButton,
    authLocalLoginSubmitButton,
    authLocalLoginUseUsernameLabel
  } = serverConfig;

  if (!isOpen) {
    return (
      <div className="open-button">
        <Button
          category="light"
          customBackgroundColor={authLocalLoginButton?.backgroundColor}
          customBackgroundColorHover={
            authLocalLoginButton?.backgroundColorHover
          }
          customColor={authLocalLoginButton?.color}
          onClick={handleOpen}
          style={{ marginTop: 15, width: '100%' }}
          text={
            openButtonText ||
            authLocalLoginButton?.textLocales?.[language] ||
            authLocalLoginButton?.text ||
            messages.authVuoAccount
          }
        />
      </div>
    );
  }

  if (isTwoFactor) {
    return (
      <LoginTwoFactor
        onCancel={handleTwoFactorCancel}
        onSuccess={onSuccess}
        next={next}
      />
    );
  }

  return (
    <div className="login-form">
      <Form onSubmit={handleSubmit}>
        {sessionUserUsername ? null : (
          <FormGroup
            htmlFor="email-or-username"
            label={
              accountEnableUsernameAccounts
                ? authLocalLoginUseUsernameLabel
                  ? messages.commonUsername
                  : `${messages.commonEmail} ${
                      messages.commonOr
                    } ${messages.commonUsername.toLowerCase()}`
                : messages.commonEmail
            }
          >
            <Text
              field={
                accountEnableUsernameAccounts ? 'emailOrUsername' : 'email'
              }
              id="email-or-username"
              inputContainerRef={(el) => (inputContainer.current = el)}
              placeholder={
                authLocalLoginUseUsernameLabel
                  ? ''
                  : messages.loginEmailPlaceholder
              }
              type="text"
            />
          </FormGroup>
        )}

        <FormGroup label={messages.commonPassword} htmlFor="password">
          <Text field="password" id="password" type="password" />
        </FormGroup>

        {error ? (
          <Alert className="login-error" text={error} type="error-light" />
        ) : null}

        <Button
          category="success"
          customBackgroundColor={authLocalLoginSubmitButton?.backgroundColor}
          customBackgroundColorHover={
            authLocalLoginSubmitButton?.backgroundColorHover
          }
          customColor={authLocalLoginSubmitButton?.color}
          loading={isSubmitting}
          style={{ marginTop: 20, width: '100%' }}
          text={messages.authSignIn}
          type="submit"
        />

        {!sessionUserUsername ? (
          <>
            <div className="forgot-password">
              <Link
                href="/reset-password"
                text={messages.authUnableToAccessAccount}
              />
            </div>

            {registrationLinkEnabled ? (
              <div className="register">
                {registrationLinkOnClick ? (
                  <Button
                    category="link"
                    onClick={registrationLinkOnClick}
                    style={{ color: theme.linkColor, fontWeight: 600 }}
                    text={messages.authRegister}
                  />
                ) : (
                  <Link href="/register" text={messages.authRegister} />
                )}
              </div>
            ) : null}
          </>
        ) : null}
      </Form>

      <style jsx>{`
        .login-form :global(.login-error) {
          margin-top: 20px;
        }
        .forgot-password {
          font-size: 1.4rem;
          margin-top: 15px;
          text-align: center;
        }
        .register {
          font-size: 1.4rem;
          margin-top: 10px;
          text-align: center;
        }
        .username-toggle :global(> button > span:hover) {
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

export default LocalLogin;
